// See [Barrel File Removal FAQ](https://hello.atlassian.net/wiki/x/KJT2aAE)
/* eslint-disable no-barrel-files/no-barrel-files */
import {
	type AggRequest,
	type AggResponse,
	fetchAgg,
} from '../../../../../../common/utils/fetch-agg';
import { generateHashFromVariables } from '../../../../../../utils/caching/common/key-generator';
import { inMemoryCache } from '../../../../../../utils/caching/in-memory';
import {
	linkedJiraIssuesCypherQueryGenerator,
	type LinkedJiraIssuesCypherQueryVariables,
} from './query-generator';
import { type CypherQueryData, type LinkedJiraIssueUrlsMap } from './types';

export type { LinkedJiraIssueUrlsMap } from './types';

/**
 * Default Cypher query for finding Jira issues linked to a pull request
 * via the `issue_associated_pr` relationship.
 */
const PR_TO_JIRA_ISSUE_CYPHER_QUERY =
	'MATCH (issue:JiraIssue)-[:issue_associated_pr]->(pr:GraphPullRequest {ari: $id}) RETURN issue';

const cache = inMemoryCache<AggResponse<CypherQueryData>>();

const expireOnErrorOrEmpty = <T>(result: AggResponse<T>) => {
	const { data, errors } = result;
	return !data || (!!errors && errors.length > 0);
};

/**
 * Extracts Jira issue URLs from a Cypher query response.
 */
function parseJiraIssueUrls(response: AggResponse<CypherQueryData>): string[] {
	const edges = response.data?.graphStore?.cypherQueryV2?.edges;
	if (!edges || edges.length === 0) {
		return [];
	}

	const urls: string[] = [];
	for (const edge of edges) {
		for (const column of edge.node.columns) {
			const webUrl = column.value?.data?.webUrl;
			if (webUrl) {
				urls.push(webUrl);
			}
		}
	}
	return urls;
}

export type FetchLinkedJiraIssueUrlsProps = Pick<
	AggRequest<Record<string, unknown>>,
	'aggAbsoluteUrl' | 'headers'
>;

/**
 * Fetches Jira issue URLs linked to an entity via the UGS Cypher query API.
 * Traverses a specified relationship in reverse direction using the Flock graph traversal engine.
 *
 * @param entityAri - The ARI of the entity to find linked Jira issues for
 * @param queryContext - The site ARI for UGS workspace routing (e.g., "ari:cloud:jira::site/<cloud-id>")
 * @param cypherQuery - The Cypher query to execute (defaults to PR → Jira issue traversal)
 * @param fetchProps - AGG request configuration (aggAbsoluteUrl, headers)
 * @returns Array of Jira issue URLs
 *
 * @see https://developer.atlassian.com/cloud/ari-graph-store/cypher/api-usage
 */
export async function fetchLinkedJiraIssueUrls(
	entityAri: string,
	queryContext: string,
	{ aggAbsoluteUrl, headers }: FetchLinkedJiraIssueUrlsProps,
	cypherQuery: string = PR_TO_JIRA_ISSUE_CYPHER_QUERY,
): Promise<string[]> {
	const graphQuery = linkedJiraIssuesCypherQueryGenerator();
	const variables: LinkedJiraIssuesCypherQueryVariables = {
		cypherQuery,
		params: { id: entityAri },
		queryContext,
	};

	const response = await cache.inMemoryDecorator(
		generateHashFromVariables({ entityAri, cypherQuery }),
		() =>
			fetchAgg<LinkedJiraIssuesCypherQueryVariables, CypherQueryData>({
				variables,
				aggAbsoluteUrl,
				graphQuery,
				operationName: 'SearchPageLinkedJiraIssuesHydration',
				headers,
			}),
		expireOnErrorOrEmpty,
	);

	return parseJiraIssueUrls(response);
}

/**
 * Fetches Jira issue URLs linked to multiple entities.
 *
 * @param entityAris - Array of entity ARIs to look up
 * @param queryContext - The site ARI for UGS workspace routing (e.g., "ari:cloud:jira::site/<cloud-id>")
 * @param fetchProps - AGG request configuration (aggAbsoluteUrl, headers)
 * @param cypherQuery - The Cypher query to execute (defaults to PR → Jira issue traversal)
 * @returns A map of entity ARI → Jira issue URLs
 */
export async function fetchLinkedJiraIssueUrlsBatch(
	entityAris: string[],
	queryContext: string,
	fetchProps: FetchLinkedJiraIssueUrlsProps,
	cypherQuery?: string,
): Promise<LinkedJiraIssueUrlsMap> {
	const results: LinkedJiraIssueUrlsMap = {};

	// Fetch in parallel for all entity ARIs
	const promises = entityAris.map(async (entityAri) => {
		try {
			const urls = await fetchLinkedJiraIssueUrls(entityAri, queryContext, fetchProps, cypherQuery);
			results[entityAri] = urls;
		} catch {
			results[entityAri] = null;
		}
	});

	await Promise.all(promises);
	return results;
}
